import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, Clock, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';

const BlogPostPage = () => {
  const { id } = useParams();

  // Blog post data
  const blogPosts = {
    'digital-governance-last-mile': {
      title: 'Digital Governance at the Last Mile',
      subtitle: 'How AEPL is Digitizing Rural Development in Central India with Zoho',
      date: 'November 2024',
      readTime: '8 min read',
      category: 'Case Study',
      image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40',
      tags: ['GovTech', 'Rural Development', 'Digital Transformation', 'Zoho'],
      content: `
        <div class="prose prose-lg max-w-none">
          <div class="bg-gradient-to-br from-gray-50 to-white p-8 rounded-2xl border-2 border-gray-200 mb-8">
            <h3 class="text-2xl font-bold text-gray-900 mb-4">Organization Profile</h3>
            <div class="grid md:grid-cols-2 gap-4">
              <div>
                <p class="text-sm font-semibold text-gray-600">Organization</p>
                <p class="text-gray-900">AEPL</p>
              </div>
              <div>
                <p class="text-sm font-semibold text-gray-600">Headquarters</p>
                <p class="text-gray-900">Central India</p>
              </div>
              <div>
                <p class="text-sm font-semibold text-gray-600">Sector</p>
                <p class="text-gray-900">GovTech / Rural Development / Civic Infrastructure</p>
              </div>
              <div>
                <p class="text-sm font-semibold text-gray-600">Zoho Suite</p>
                <p class="text-gray-900">Desk, CRM, MDM, Mail, Cliq, Meeting</p>
              </div>
            </div>
          </div>

          <h2>1. Executive Summary</h2>
          <p>In the rural hinterlands of Central India, the gap between government policy and on-ground execution is often widened by a lack of digital infrastructure. <strong>AEPL</strong> has stepped in to close this gap. By repurposing enterprise-grade cloud software for civic governance, AEPL has built a comprehensive ecosystem that manages public grievances, tracks construction projects, and empowers field agents.</p>
          
          <p>Using the Zoho ecosystem, AEPL has moved from manual, paper-heavy processes to a transparent, data-driven operation that <strong>resolves 85% of citizen complaints monthly</strong> and manages critical infrastructure projects across hundreds of Gram Panchayats.</p>

          <h2>2. The Challenge: The Friction of Manual Governance</h2>
          <p>Before digital intervention, the administrative landscape faced significant hurdles:</p>
          
          <div class="grid md:grid-cols-2 gap-6 my-8">
            <div class="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-lg">
              <h4 class="font-bold text-red-900 mb-2">Data Silos</h4>
              <p class="text-red-800">Information regarding broken roads or hospital shortages was trapped in paper applications or verbal complaints, making it impossible to track trends.</p>
            </div>
            <div class="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-lg">
              <h4 class="font-bold text-red-900 mb-2">Accountability Gaps</h4>
              <p class="text-red-800">Without a ticketing system, public requests were often lost in transit between departments.</p>
            </div>
            <div class="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-lg">
              <h4 class="font-bold text-red-900 mb-2">Digital Exclusion</h4>
              <p class="text-red-800">Field agents responsible for distributing sanitary pads and conducting surveys lacked personal devices and technical literacy.</p>
            </div>
            <div class="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-lg">
              <h4 class="font-bold text-red-900 mb-2">The Need</h4>
              <p class="text-red-800">A solution that was scalable, easy to deploy, and capable of functioning where "customer support" means "citizen welfare."</p>
            </div>
          </div>

          <h2>3. The Solution: A Three-Pronged Digital Transformation</h2>

          <div class="bg-cyan-50 border-2 border-cyan-200 p-8 rounded-2xl my-8">
            <h3 class="text-2xl font-bold text-cyan-900 mb-4">Phase I: Zoho Desk as a Civic Command Center</h3>
            <p class="text-cyan-900">AEPL reimagined Zoho Desk not just as a support tool, but as a <strong>Complaint Management System (CMS)</strong> for the public sector.</p>
          </div>

          <h4>Omnichannel Capture</h4>
          <p>Citizens in rural areas have varying levels of digital access. AEPL configured Zoho Desk to ingest tickets via:</p>
          <ul>
            <li>Email</li>
            <li>Website</li>
            <li>WhatsApp</li>
            <li>Phone</li>
            <li>Physical Walk-ins</li>
          </ul>
          <p>This ensures no citizen is excluded due to a lack of technology.</p>

          <h4>Automated Routing & SLAs</h4>
          <ol>
            <li>A ticket is created (e.g., "Water supply failure in Ramtek") and generates a <strong>Unique ID</strong></li>
            <li>Zoho Desk's assignment rules automatically route the ticket to the specific government liaison or department</li>
            <li>Service Level Agreements (SLAs) track the "Resolution Rate," flagging tickets that remain open past promised timeframe</li>
          </ol>

          <h4>The Feedback Loop (SMS Integration)</h4>
          <p>Recognizing that many villagers do not check email, AEPL integrated an SMS gateway:</p>
          <div class="bg-gray-100 p-6 rounded-lg font-mono text-sm my-6">
            Ticket Created → SMS to Citizen → Department Update → SMS Update → Resolution → Closure SMS
          </div>

          <h4>Data-Driven Policy Making</h4>
          <p>By tagging tickets with specific domains, AEPL provides the government with heatmaps of public needs. Current monthly data reveals:</p>
          
          <div class="grid md:grid-cols-3 gap-4 my-6">
            <div class="bg-white border-2 border-gray-200 p-6 rounded-xl text-center">
              <div class="text-4xl font-bold text-cyan-600 mb-2">30%</div>
              <div class="font-semibold text-gray-900">Healthcare</div>
              <p class="text-sm text-gray-600 mt-2">Medicine shortages, ambulance delays, clinic sanitation</p>
            </div>
            <div class="bg-white border-2 border-gray-200 p-6 rounded-xl text-center">
              <div class="text-4xl font-bold text-cyan-600 mb-2">25%</div>
              <div class="font-semibold text-gray-900">Construction</div>
              <p class="text-sm text-gray-600 mt-2">Potholes, illegal construction, streetlights</p>
            </div>
            <div class="bg-white border-2 border-gray-200 p-6 rounded-xl text-center">
              <div class="text-4xl font-bold text-cyan-600 mb-2">20%</div>
              <div class="font-semibold text-gray-900">Documents</div>
              <p class="text-sm text-gray-600 mt-2">Aadhaar, PAN, biometric issues</p>
            </div>
          </div>

          <div class="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-lg my-8">
            <p class="text-green-900"><strong>Impact:</strong> The system currently achieves an <strong>85% monthly resolution rate</strong>, with a strategic roadmap to achieve 100% closure on actionable tickets.</p>
          </div>

          <div class="bg-cyan-50 border-2 border-cyan-200 p-8 rounded-2xl my-8">
            <h3 class="text-2xl font-bold text-cyan-900 mb-4">Phase II: Zoho CRM for Infrastructure Lifecycle Management</h3>
            <p class="text-cyan-900">AEPL utilizes Zoho CRM in a highly creative way: treating <strong>Construction Projects as "Leads" and "Deals"</strong>.</p>
          </div>

          <h4>The "Road-as-a-Lead" Workflow</h4>
          <p>Instead of sales prospects, the CRM tracks development works (e.g., <em>Ramtek to Mansar Road Resurfacing</em>). The "Sales Cycle" is replaced by the <strong>Construction Lifecycle</strong>:</p>
          
          <div class="flex flex-wrap gap-2 my-6">
            <span class="bg-gray-200 text-gray-900 px-4 py-2 rounded-full font-medium">Proposed</span>
            <span class="bg-yellow-200 text-yellow-900 px-4 py-2 rounded-full font-medium">Waiting for Approval</span>
            <span class="bg-blue-200 text-blue-900 px-4 py-2 rounded-full font-medium">Sanctioned</span>
            <span class="bg-purple-200 text-purple-900 px-4 py-2 rounded-full font-medium">In-Progress</span>
            <span class="bg-green-200 text-green-900 px-4 py-2 rounded-full font-medium">Completed</span>
            <span class="bg-red-200 text-red-900 px-4 py-2 rounded-full font-medium">Pending/Stalled</span>
          </div>

          <h4>Solving the Geography Problem with Multi-Select</h4>
          <p>A unique challenge in rural infrastructure is that a single road or canal often passes through the jurisdiction of multiple villages.</p>
          
          <div class="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-r-lg my-6">
            <p class="text-amber-900"><strong>Feature Utilization:</strong> AEPL uses the Multi-Select Picklist feature to tag multiple Gram Panchayats to a single project record.</p>
            <p class="text-amber-900 mt-2"><strong>Benefit:</strong> If a Gram Panchayat head asks, "What work is happening in my village?", the system can instantly pull records for roads, wells, and buildings associated with that specific location.</p>
          </div>

          <div class="bg-cyan-50 border-2 border-cyan-200 p-8 rounded-2xl my-8">
            <h3 class="text-2xl font-bold text-cyan-900 mb-4">Phase III: Bridging the Digital Divide with Zoho MDM</h3>
            <p class="text-cyan-900">One of AEPL's critical social initiatives is the distribution of sanitary pads. This relies on a network of <strong>120 delivery agents</strong>.</p>
          </div>

          <h4>The Problem</h4>
          <p>Most agents were women with low digital literacy who relied on shared family phones. They could not be expected to troubleshoot app errors or configure settings.</p>

          <h4>The MDM Solution</h4>
          <p>AEPL distributed company-provisioned smartphones to all 120 agents, pre-loaded with <strong>Zoho MDM (Mobile Device Management)</strong>.</p>

          <h4>Remote Management & Upskilling</h4>
          <ul>
            <li><strong>Zero-Touch Updates:</strong> When the delivery app needs an update, IT pushes it remotely. The agent doesn't need to visit the office.</li>
            <li><strong>Route Management:</strong> Logistics teams can push route maps and schedules directly to device screens.</li>
            <li><strong>Remote Troubleshooting:</strong> IT can remotely view the screen and fix settings, effectively "training" the agent without travel costs.</li>
          </ul>

          <h2>4. Operational Efficiency: The IT Backbone</h2>
          <p>To support this massive external operation, AEPL's internal IT team relies on the communication suite:</p>
          
          <div class="grid md:grid-cols-3 gap-6 my-8">
            <div class="bg-white border-2 border-gray-200 p-6 rounded-xl">
              <h4 class="font-bold text-gray-900 mb-2">Zoho Cliq</h4>
              <p class="text-gray-600">Serves as the nerve center for real-time communication between office staff and field supervisors.</p>
            </div>
            <div class="bg-white border-2 border-gray-200 p-6 rounded-xl">
              <h4 class="font-bold text-gray-900 mb-2">Zoho Meeting</h4>
              <p class="text-gray-600">Facilitates virtual reviews with government officials and internal training sessions.</p>
            </div>
            <div class="bg-white border-2 border-gray-200 p-6 rounded-xl">
              <h4 class="font-bold text-gray-900 mb-2">Zoho Mail</h4>
              <p class="text-gray-600">Provides professional, unified communication layer for official correspondence.</p>
            </div>
          </div>

          <h2>5. Key Metrics of Success</h2>
          <p>AEPL measures the success of this digital transformation through three core KPIs:</p>
          
          <div class="space-y-4 my-8">
            <div class="flex items-start gap-4 bg-white border-2 border-gray-200 p-6 rounded-xl">
              <div class="w-8 h-8 bg-cyan-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <CheckCircle2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <h4 class="font-bold text-gray-900 mb-1">Average Resolution Time</h4>
                <p class="text-gray-600">Tracking the speed of public grievance redressal to ensure government accountability.</p>
              </div>
            </div>
            <div class="flex items-start gap-4 bg-white border-2 border-gray-200 p-6 rounded-xl">
              <div class="w-8 h-8 bg-cyan-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <CheckCircle2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <h4 class="font-bold text-gray-900 mb-1">SLA Adherence</h4>
                <p class="text-gray-600">Monitoring how often the system meets its service commitments to the public.</p>
              </div>
            </div>
            <div class="flex items-start gap-4 bg-white border-2 border-gray-200 p-6 rounded-xl">
              <div class="w-8 h-8 bg-cyan-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <CheckCircle2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <h4 class="font-bold text-gray-900 mb-1">Community Impact Reports</h4>
                <p class="text-gray-600">Transforming "complaints" into "resolved case studies" that build public trust.</p>
              </div>
            </div>
          </div>

          <div class="bg-gradient-to-br from-cyan-500 to-blue-600 text-white p-8 rounded-2xl my-12">
            <h2 class="text-white mb-4">Conclusion</h2>
            <p class="text-white">AEPL has demonstrated that enterprise software is not just for profit—it is a powerful engine for social change. By adapting Zoho Desk for public grievances, Zoho CRM for civic works, and Zoho MDM for digital inclusion, AEPL has built a scalable model for rural development.</p>
            <p class="text-white mt-4">They have effectively turned the concept of "Smart Cities" into a reality for <strong>"Smart Villages"</strong> in Central India.</p>
          </div>
        </div>
      `
    }
  };

  const post = blogPosts[id];

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Post Not Found</h1>
          <Link to="/blog" className="text-cyan-600 hover:text-cyan-700">
            ← Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Back Button */}
      <div className="bg-white border-b border-gray-200 sticky top-20 z-40">
        <div className="container-custom py-4">
          <Link 
            to="/blog"
            className="inline-flex items-center gap-2 text-gray-600 hover:text-cyan-600 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Blog
          </Link>
        </div>
      </div>

      {/* Hero Image */}
      <div className="relative h-96 md:h-[500px] overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${post.image})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/50 to-transparent" />
      </div>

      {/* Content */}
      <div className="container-custom -mt-32 relative z-10">
        <div className="max-w-4xl mx-auto">
          {/* Article Header */}
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-2xl mb-12">
            <span className="bg-cyan-500 text-white px-4 py-1.5 rounded-full text-sm font-medium">
              {post.category}
            </span>
            
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mt-6 mb-4">
              {post.title}
            </h1>
            
            <p className="text-xl text-gray-600 mb-6">
              {post.subtitle}
            </p>

            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 pb-6 border-b border-gray-200">
              <div className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                <span>{post.date}</span>
              </div>
              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                <span>{post.readTime}</span>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mt-6">
              {post.tags.map((tag, idx) => (
                <span
                  key={idx}
                  className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {/* Article Body */}
          <div 
            className="bg-white rounded-3xl p-8 md:p-12 shadow-xl mb-12"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />

          {/* CTA */}
          <div className="bg-gradient-to-br from-gray-50 to-white rounded-3xl p-8 md:p-12 text-center border-2 border-gray-200">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">
              Interested in Similar Solutions?
            </h3>
            <p className="text-lg text-gray-600 mb-6">
              Discover how AEPL can help transform your constituency
            </p>
            <Button
              size="lg"
              className="bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white px-12 py-6 text-lg rounded-2xl"
              asChild
            >
              <Link to="/contact">Get in Touch</Link>
            </Button>
          </div>
        </div>
      </div>

      {/* Bottom Spacing */}
      <div className="h-24" />
    </div>
  );
};

export default BlogPostPage;
